// =============================================
// UTILITÁRIOS DE VALIDAÇÃO DE USUÁRIO
// =============================================

import { supabase } from '../lib/supabase'

// Regex para validação de email
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Interface para resultado de validação
export interface ValidationResult {
  isValid: boolean
  error?: string
}

// Interface para resultado de verificação de usuário
export interface UserCheckResult {
  exists: boolean
  error?: string
  userInfo?: any
}

/**
 * Valida formato de email
 */
export const validateEmailFormat = (email: string): ValidationResult => {
  if (!email || !email.trim()) {
    return {
      isValid: false,
      error: 'E-mail é obrigatório'
    }
  }

  const trimmedEmail = email.trim()
  
  if (!EMAIL_REGEX.test(trimmedEmail)) {
    return {
      isValid: false,
      error: 'E-mail deve ter um formato válido (exemplo@dominio.com)'
    }
  }

  return {
    isValid: true
  }
}

/**
 * Valida senha
 */
export const validatePassword = (password: string, confirmPassword?: string): ValidationResult => {
  if (!password) {
    return {
      isValid: false,
      error: 'Senha é obrigatória'
    }
  }

  if (password.length < 6) {
    return {
      isValid: false,
      error: 'A senha deve ter pelo menos 6 caracteres'
    }
  }

  if (confirmPassword !== undefined && password !== confirmPassword) {
    return {
      isValid: false,
      error: 'As senhas não coincidem'
    }
  }

  return {
    isValid: true
  }
}

/**
 * Valida nome
 */
export const validateName = (name: string): ValidationResult => {
  if (!name || !name.trim()) {
    return {
      isValid: false,
      error: 'Nome é obrigatório'
    }
  }

  return {
    isValid: true
  }
}

/**
 * Verifica se um usuário já existe no sistema
 */
export const checkUserExists = async (email: string): Promise<UserCheckResult> => {
  try {
    // Primeiro valida o formato do email
    const emailValidation = validateEmailFormat(email)
    if (!emailValidation.isValid) {
      return {
        exists: false,
        error: emailValidation.error
      }
    }

    // Tenta usar a função SQL se disponível
    try {
      const { data: userExists, error: checkError } = await supabase
        .rpc('check_user_exists', { user_email: email.trim() })

      if (checkError) {
        // Se a função não existir ou der erro, retorna que não existe para continuar
        return {
          exists: false,
          error: checkError.message
        }
      }

      return {
        exists: userExists || false
      }
    } catch (error) {
      // Se a função não existir, retorna que não existe para continuar
      return {
        exists: false,
        error: 'Função de verificação não disponível'
      }
    }
  } catch (error) {
    return {
      exists: false,
      error: 'Erro inesperado ao verificar usuário'
    }
  }
}

/**
 * Validação completa do formulário de criação de usuário
 */
export const validateUserForm = (formData: {
  name: string
  email: string
  password: string
  confirmPassword: string
  user_group_id: string
}): ValidationResult => {
  // Validar nome
  const nameValidation = validateName(formData.name)
  if (!nameValidation.isValid) {
    return nameValidation
  }

  // Validar email
  const emailValidation = validateEmailFormat(formData.email)
  if (!emailValidation.isValid) {
    return emailValidation
  }

  // Validar senha
  const passwordValidation = validatePassword(formData.password, formData.confirmPassword)
  if (!passwordValidation.isValid) {
    return passwordValidation
  }

  // Validar grupo
  if (!formData.user_group_id) {
    return {
      isValid: false,
      error: 'Selecione um grupo de usuário'
    }
  }

  return {
    isValid: true
  }
}

/**
 * Trata erros específicos do Supabase Auth
 */
export const handleAuthError = (error: any): string => {
  if (error.message.includes('Unable to validate email address')) {
    return 'Erro ao criar usuário: E-mail inválido. Tente novamente.'
  } else if (error.message.includes('User already registered') || 
             error.message.includes('already been registered') ||
             error.message.includes('already exists') ||
             error.message.includes('duplicate') ||
             error.code === '23505') {
    return 'Erro ao criar usuário: Este e-mail já está cadastrado. Use outro e-mail.'
  } else if (error.message.includes('Password should be at least')) {
    return 'Erro ao criar usuário: A senha deve ter pelo menos 6 caracteres.'
  } else if (error.message.includes('Invalid login credentials')) {
    return 'Erro ao criar usuário: E-mail ou senha inválidos.'
  } else if (error.message.includes('Signup is disabled')) {
    return 'Erro ao criar usuário: Cadastro de novos usuários está desabilitado.'
  } else {
    return `Erro ao criar usuário: ${error.message}`
  }
}

/**
 * Trata erros específicos do banco de dados (perfil)
 */
export const handleProfileError = (error: any): string => {
  if (error.message.includes('duplicate key') || error.code === '23505') {
    return 'Erro ao criar usuário: Este usuário já existe no sistema. Tente com outro e-mail.'
  } else if (error.message.includes('foreign key') || error.code === '23503') {
    return 'Erro ao criar usuário: Grupo de usuário inválido.'
  } else if (error.message.includes('permission denied') || error.code === '42501') {
    return 'Erro ao criar usuário: Sem permissão para criar usuários.'
  } else if (error.message.includes('unique constraint')) {
    return 'Erro ao criar usuário: Este e-mail já está em uso. Use outro e-mail.'
  } else {
    return `Erro ao criar usuário: ${error.message}`
  }
}
